import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { PAYMENTTYPES } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { clearRenewDetails } from "./organizationSlice";
import {
  getRenewDetails,
  getRenewHistory,
  insertRenewForm,
  updateRenewForm,
} from "./services/organization.services";
import moment from "moment";

const RenewSubscriptionForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const [formKey, setFormKey] = React.useState(1);
  const RenewDetails = useAppSelector(
    (state) => state.organization.RenewDetails
  );
  const RenewID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.organization.formLoading);

  useEffect(() => {
    return () => {
      dispatch(clearRenewDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (RenewID) {
      dispatch(getRenewDetails(RenewID));
    }
  }, [RenewID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [RenewDetails]);

  const handleSubmit = async (values: any) => {
    try {
      if (!RenewID) {
        const insertPayload = {
          RenewDate: values.RenewDate
            ? moment(values.RenewDate).format("YYYY-MM-DD")
            : "",
          TotalUser: values.TotalUser,
          PaymentType: values.PaymentType,
          Amount: values.Amount,
          Remarks: values.Remarks,
        };
        const response = await dispatch(insertRenewForm(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRenewDetails());
          dispatch(getRenewHistory());
        }
      } else {
        const editPayload = {
          ID: RenewID,
          RenewDate: values.RenewDate
            ? moment(values.RenewDate).format("YYYY-MM-DD")
            : "",
          TotalUser: values.TotalUser,
          PaymentType: values.PaymentType,
          Amount: values.Amount,
          Remarks: values.Remarks,
        };
        const response = await dispatch(updateRenewForm(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRenewDetails());
          dispatch(getRenewHistory());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={RenewDetails}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {RenewID ? "Update Renew" : "Add Renew"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name={`RenewDate`}
                  label="Renew Date"
                  format="dd/MM/yyyy"
                  component={FormDatePicker}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="TotalUser"
                  astrike={true}
                  label="Total User"
                  placeholder="Total User"
                  component={FormNumericTextField}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"PaymentType"}
                  name={"PaymentType"}
                  astrike={true}
                  label={"Payment Type"}
                  placeholder="i.e. Yearly"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={PAYMENTTYPES?.map((type: any) => {
                    return {
                      value: type?.value,
                      label: type?.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="Amount"
                  astrike={true}
                  label="Amount"
                  placeholder="Amount"
                  component={FormNumericTextField}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <GridLayoutItem>
                  <Field
                    name="Remarks"
                    label="Remarks"
                    component={FormTextArea}
                  />
                </GridLayoutItem>
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={RenewID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default RenewSubscriptionForm;
